import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  MapPin, Calendar, Gift, Heart, CheckCircle, ChevronRight, Map as MapIcon, 
  Clock, Camera, CreditCard, MessageCircle, Send, User, 
  Play, Pause, Volume2, VolumeX, Church, GlassWater, Music, Utensils, PartyPopper, Moon,
  Shirt, Info
} from 'lucide-react';

const App = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [rsvpMethod, setRsvpMethod] = useState('direct');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  
  // Estados para la animación de la carta
  const [envelopeOpen, setEnvelopeOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // --- CONFIGURACIÓN DE AUDIO ---
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  // URL de tu archivo MP3 local
  const MUSIC_URL = "/boda-musica.mp3"; 

  // --- URL de tu Google Apps Script ---
  const GOOGLE_SHEETS_URL = "https://script.google.com/macros/s/AKfycbxHXmtgVwIOqIMiW1yM32OLRMmkSuC_lkUQE9P4z0jkCZdjA2GffYyXZVHue9LPP8Un/exec";

  const weddingDate = useMemo(() => new Date('2026-08-01T14:00:00'), []);

  // Parámetros dinámicos (?n=Nombre&p=2)
  const [params] = useState(() => {
    const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const n = urlParams.get('n')?.replace(/_/g, ' ') || 'Invitado Especial';
    const p = parseInt(urlParams.get('p')) || 1;
    return { nombre: n, pases: p };
  });

  const [formData, setFormData] = useState({
    nombre: params.nombre,
    pasesConfirmados: params.pases
  });

  const telefonoWA = "5215610574027"; 
  const mensajeWA = `https://wa.me/${telefonoWA}?text=¡Hola!%20Soy%20${encodeURIComponent(formData.nombre)}%20y%20confirmo%20mi%20asistencia%20con%20${formData.pasesConfirmados}%20pases%20para%20la%20boda%20de%20Nashelly%20y%20Salvador.`;

  const colors = {
    navyBlue: '#1a2b44',
    gold: '#c5a059',
    white: '#ffffff',
    paper: '#fdfbf7'
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = weddingDate.getTime() - now.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [weddingDate]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = () => {
    if (isPlaying) { audioRef.current.pause(); } 
    else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => setCurrentTime(audioRef.current.currentTime);
  const handleLoadedMetadata = () => setDuration(audioRef.current.duration);
  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleOpenLetter = () => {
    setEnvelopeOpen(true);
    setTimeout(() => {
      setShowContent(true);
      window.scrollTo(0, 0);
    }, 1500);
  };

  const handleDirectConfirm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, fecha: new Date().toLocaleString() })
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showContent) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a121e] overflow-hidden select-none">
        <style>
          {`
            .envelope-wrapper { position: relative; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #0a121e; }
            .main-envelope-image { position: relative; width: 800px; height: 533px; max-width: 95vw; background-image: url("/sobre.png"); background-size: cover; background-position: center; background-repeat: no-repeat; box-shadow: 0 40px 80px rgba(0,0,0,0.6); transition: transform 1.5s cubic-bezier(0.7, 0, 0.3, 1), opacity 1s ease; display: flex; align-items: center; justify-content: center; border-radius: 4px; }
            .main-envelope-image.opening { transform: translateY(-100%) scale(0.8); opacity: 0; }
            
            .invisible-seal-button { 
              position: absolute; 
              top: 52%; 
              left: 50%; 
              transform: translate(-50%, -50%); 
              width: 140px; 
              height: 140px; 
              border-radius: 50%; 
              cursor: pointer; 
              background: transparent; 
              border: none; 
              outline: none; 
              z-index: 20; 
            }
            
            .tap-hint { 
              position: absolute; 
              bottom: -50px; 
              left: 50%; 
              transform: translateX(-50%); 
              color: white; 
              font-family: 'Inter', sans-serif; 
              font-size: 0.7rem; 
              text-transform: uppercase; 
              letter-spacing: 4px; 
              opacity: 0.4; 
              animation: blink 2s infinite; 
              white-space: nowrap; 
              pointer-events: none; 
            }
            
            @keyframes blink { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.7; } }
          `}
        </style>
        <div className="envelope-wrapper">
          <div className={`main-envelope-image ${envelopeOpen ? 'opening' : ''}`}>
            <button className="invisible-seal-button" onClick={handleOpenLetter} aria-label="Abrir invitación">
              <span className="tap-hint">Toca el sello para abrir</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen selection:bg-blue-100 font-sans animate-fade-in bg-white text-slate-800">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;600&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&display=swap');
          .font-sans { font-family: 'Inter', sans-serif; }
          .font-serif { font-family: 'Playfair Display', serif; }
          .font-book { font-family: 'Libre Baskerville', serif; }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .animate-fade-up { animation: fadeInUp 1.2s ease-out forwards; }
          .animate-fade-in { animation: fadeIn 1.5s ease-out forwards; }
          .bg-invitation { background-image: url("https://www.transparenttextures.com/patterns/paper-fibers.png"); }
          
          input[type=range].player-slider { -webkit-appearance: none; background: #e2e8f0; height: 4px; border-radius: 2px; outline: none; }
          input[type=range].player-slider::-webkit-slider-thumb { -webkit-appearance: none; height: 14px; width: 14px; border-radius: 50%; background: ${colors.gold}; cursor: pointer; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          
          input[type=range].rsvp-slider::-webkit-slider-runnable-track { background: #f1f5f9; border-radius: 10px; height: 8px; }
          input[type=range].rsvp-slider::-webkit-slider-thumb { -webkit-appearance: none; height: 24px; width: 24px; border-radius: 50%; background: #1a2b44; cursor: pointer; margin-top: -8px; border: 2px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }

          .timeline-line::before {
            content: '';
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            width: 2px;
            height: 100%;
            background-color: #f1f5f9;
            z-index: 0;
          }
        `}
      </style>

      <audio ref={audioRef} src={MUSIC_URL} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} loop />

      {/* Portada Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/boda54.jpg" className="w-full h-full object-cover opacity-100 transition-opacity duration-1000" alt="Boda" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(10, 18, 30, 0.6) 0%, rgba(10, 18, 30, 0.2) 40%, transparent 100%)` }}></div>
        </div>
        <div className="relative z-10 text-center px-4 space-y-12 animate-fade-up">
          <span className="uppercase tracking-[0.6em] text-[10px] text-white/90 font-sans bg-black/20 backdrop-blur-sm px-6 py-2 rounded-full inline-block shadow-sm">¡Nos casamos!</span>
          <h1 className="text-7xl md:text-9xl font-serif text-white leading-none drop-shadow-2xl">Nashelly <br /> <span className="text-5xl italic font-light">&</span> <br /> Salvador</h1>
          <div className="flex justify-center items-center space-x-6 md:space-x-12 text-2xl font-light tracking-[0.3em] text-white">
            <span className="font-sans">01</span> <div className="h-px w-10 bg-white/40"></div> 
            <span className="font-serif italic font-bold text-6xl md:text-8xl lg:text-9xl tracking-widest text-white drop-shadow-xl">AGOSTO</span> 
            <div className="h-px w-10 bg-white/40"></div> <span className="font-sans">2026</span>
          </div>
          <div className="pt-8 flex justify-center space-x-10 text-white drop-shadow-md">
            {Object.entries(timeLeft).map(([unit, value]) => (
              <div key={unit} className="flex flex-col items-center">
                <span className="text-4xl md:text-6xl font-serif font-light">{value}</span>
                <span className="text-[10px] uppercase tracking-[0.3em] opacity-80 mt-2 font-sans">{unit === 'days' ? 'Días' : unit === 'hours' ? 'Hrs' : unit === 'minutes' ? 'Min' : 'Seg'}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frase y Música */}
      <section className="py-24 px-6 bg-white text-center">
        <div className="max-w-3xl mx-auto space-y-8 animate-fade-up">
          <Heart size={20} className="mx-auto text-slate-300 fill-slate-50" />
          <h2 className="text-3xl md:text-5xl font-serif italic text-slate-800 leading-tight">"Juntar dos mundos para crear el nuestro"</h2>
          <p className="text-lg md:text-xl font-light text-slate-500 italic font-serif">Creo en las coincidencias, porque en ellas te encontré a ti.</p>
          
          <div className="mt-12 max-w-sm mx-auto p-6 bg-[#fcfaf7] border border-slate-100 rounded-3xl shadow-sm">
            <div className="flex items-center space-x-5">
              <button onClick={togglePlay} className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-all" style={{ backgroundColor: colors.navyBlue }}>
                {isPlaying ? <Pause size={24} fill="white" /> : <Play size={24} fill="white" className="ml-1" />}
              </button>
              <div className="flex-1 space-y-2">
                <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} className="player-slider w-full cursor-pointer" />
                <div className="flex justify-between text-[10px] text-slate-400 uppercase tracking-tighter">
                  <span>{formatTime(currentTime)}</span><span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end mt-4 space-x-3 opacity-60">
               <button onClick={() => setIsMuted(!isMuted)}>{isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}</button>
               <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="player-slider w-20" />
            </div>
          </div>
        </div>
      </section>

      {/* Ubicaciones */}
      <section className="py-24 px-6 relative bg-invitation border-y border-slate-100 bg-white text-center">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-serif italic mb-4 tracking-tight text-slate-800">Donde todo comienza</h2>
            <div className="h-1 w-20 mx-auto" style={{ backgroundColor: colors.gold }}></div>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-12 shadow-sm border-t-8 rounded-b-2xl border-slate-100" style={{ borderTopColor: '#d8d2cb' }}>
              <MapPin className="mx-auto mb-6" size={32} style={{ color: '#1a2b44' }} />
              <h3 className="text-3xl font-serif mb-6 text-center">Ceremonia Religiosa</h3>
              <p className="text-center text-lg font-semibold uppercase mb-10 font-sans">Parroquia de Nuestra Señora de Fátima<br/><span className="text-2xl font-serif italic normal-case block mt-2" style={{ color: '#1a2b44' }}>14:00 hrs</span></p>
              <a href="https://maps.app.goo.gl/fmv8bbnABEMKWSQA7" target="_blank" rel="noopener noreferrer" className="block text-center py-4 border text-xs uppercase tracking-widest transition-all hover:bg-slate-50 font-sans" style={{ borderColor: '#1a2b44', color: '#1a2b44' }}>Ver Ubicación</a>
            </div>
            <div className="p-12 text-white shadow-2xl relative overflow-hidden rounded-2xl" style={{ backgroundColor: '#1a2b44' }}>
              <MapIcon className="mx-auto mb-6 text-white/50" size={32} />
              <h3 className="text-3xl font-serif mb-6 text-center text-white">Boda Civil & Recepción</h3>
              <p className="text-center text-lg font-semibold uppercase mb-10 font-sans">Hacienda San Andrés<br/><span className="text-2xl font-serif italic normal-case block mt-2" style={{ color: '#d8d2cb' }}>16:00 hrs</span></p>
              <a href="https://maps.app.goo.gl/75oETXUyUhP6zrwF7" target="_blank" rel="noopener noreferrer" className="block text-center py-4 border text-xs uppercase tracking-widest transition-all hover:bg-white/10 font-sans" style={{ borderColor: '#d8d2cb', color: '#d8d2cb' }}>¿Cómo llegar?</a>
            </div>
          </div>
        </div>
      </section>

      {/* --- ITINERARIO DE ACTIVIDADES --- */}
      <section className="py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-5xl font-serif italic text-slate-800">Itinerario</h2>
            <div className="h-px w-24 bg-slate-200 mx-auto"></div>
          </div>

          <div className="relative timeline-line">
            <div className="space-y-16 relative z-10">
              {[
                { time: '14:00 PM', event: 'Iglesia', icon: <Church size={24} />, desc: 'Misa en la Parroquia Nuestra Señora de Fátima' },
                { time: '16:30 PM', event: 'BODA CIVIL', icon: <Heart size={24} />, desc: 'Enlace legal en los jardines de la Hacienda' },
                { time: '17:00 PM', event: 'COCTEL DE BIENVENIDA', icon: <GlassWater size={24} />, desc: 'Bebidas refrescantes y canapés de bienvenida' },
                { time: '18:30 PM', event: 'PRIMER BAILE, VALS Y BRINDIS', icon: <Music size={24} />, desc: 'Momento especial y palabras de honor' },
                { time: '19:00 PM', event: 'CENA', icon: <Utensils size={24} />, desc: 'Banquete especial para celebrar nuestra unión' },
                { time: '20:30 PM', event: 'FIESTA', icon: <PartyPopper size={24} />, desc: '¡A celebrar y bailar toda la noche!' },
                { time: '12:00 AM', event: 'FIN', icon: <Moon size={24} />, desc: 'Gracias por compartir este día con nosotros' }
              ].map((item, idx) => (
                <div key={idx} className={`flex flex-col md:flex-row items-center justify-center md:space-x-12 ${idx % 2 === 0 ? '' : 'md:flex-row-reverse md:space-x-reverse'}`}>
                  {/* Texto */}
                  <div className={`flex-1 text-center ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} w-full md:w-1/2 px-4`}>
                    <span className="text-2xl font-serif italic text-slate-400 block mb-1">{item.time}</span>
                    <h4 className="text-xl font-bold text-slate-800 uppercase tracking-wide">{item.event}</h4>
                    <p className="text-sm text-slate-500 font-light mt-1">{item.desc}</p>
                  </div>
                  
                  {/* Icono */}
                  <div className="my-6 md:my-0 p-5 bg-white border border-slate-100 rounded-full shadow-lg z-20 flex items-center justify-center" style={{ color: colors.gold }}>
                    {item.icon}
                  </div>

                  {/* Espacio Vacío */}
                  <div className="flex-1 hidden md:block w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Foto después del itinerario */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl border-8 border-slate-50">
          <img 
            src="/boda84.jpg" 
            alt="Detalle Boda" 
            className="w-full h-auto object-cover min-h-[400px]"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=2000"; }}
          />
        </div>
      </section>

      {/* --- CÓDIGO DE VESTIMENTA --- */}
      <section className="py-24 px-6 bg-slate-50 border-y border-slate-100 text-center">
        <div className="max-w-3xl mx-auto space-y-10 animate-fade-up">
          <Shirt size={40} className="mx-auto text-slate-400" />
          <h2 className="text-5xl font-serif italic text-slate-800">Código de Vestimenta</h2>
          <div className="space-y-4">
             <p className="text-2xl font-serif text-slate-700 tracking-widest uppercase">Elegante</p>
             <p className="text-lg text-slate-500 font-light italic font-book px-8 leading-relaxed">
               "Con cariño les pedimos evitar prendas en color blanco y tonos similares"
             </p>
          </div>
          <div className="flex justify-center">
            <div className="h-px w-24 bg-slate-200"></div>
          </div>
        </div>
      </section>

      {/* Regalos (Mesa de Regalos) - REUBICADO */}
      <section className="py-24 px-6 bg-white border-b border-slate-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-10 animate-fade-up text-left">
            <div className="space-y-4">
              <Gift size={40} style={{ color: colors.gold }} />
              <h2 className="text-5xl font-serif italic text-slate-800">Mesa de Regalos</h2>
              <p className="text-lg text-slate-600 font-light leading-relaxed">Vuestra presencia es lo más importante para nosotros, pero si deseáis tener un detalle, aquí os dejamos algunas sugerencias.</p>
            </div>
            <div className="space-y-4 font-sans text-left">
              <div className="flex items-center justify-between p-6 bg-white border border-slate-100 shadow-sm rounded-xl group transition-all hover:shadow-md cursor-pointer">
                <div><p className="font-bold text-slate-800 uppercase">LIVERPOOL</p><p className="text-xs text-slate-400 uppercase tracking-tighter">Evento: 5029384</p></div>
                <ChevronRight size={18} style={{ color: colors.gold }} />
              </div>
              <div className="flex items-center justify-between p-6 bg-white border border-slate-100 shadow-sm rounded-xl group transition-all hover:shadow-md cursor-pointer">
                <div><p className="font-bold text-slate-800 uppercase">AMAZON</p><p className="text-xs text-slate-400 uppercase tracking-tighter">Boda Nashelly & Salvador 2026</p></div>
                <ChevronRight size={18} style={{ color: colors.gold }} />
              </div>
              <div className="mt-8 p-8 border-2 border-dashed rounded-3xl bg-white/50" style={{ borderColor: colors.gold }}>
                <div className="flex items-center space-x-3 mb-6" style={{ color: colors.navyBlue }}><CreditCard size={24} /><span className="font-semibold uppercase tracking-widest text-sm">Transferencia</span></div>
                <div className="space-y-4 text-sm font-sans">
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-400 uppercase text-[10px]">Banco</span><span className="font-bold text-slate-800 uppercase">BBVA</span></div>
                  <div className="flex justify-between border-b pb-2"><span className="text-slate-400 uppercase text-[10px]">Titular</span><span className="font-bold text-slate-800 uppercase tracking-tighter">Nashelly & Salvador</span></div>
                  <div className="pt-2"><p className="text-slate-400 uppercase text-[10px] mb-1">CLABE</p><p className="font-mono font-bold text-lg tracking-wider text-left text-slate-800">012 345 6789 1234 5678</p></div>
                </div>
              </div>
            </div>
          </div>
          <div className="relative animate-fade-in hidden md:block">
             <img src="https://images.unsplash.com/photo-1513151233558-d860c5398176?auto=format&fit=crop&q=80&w=800" className="rounded-xl shadow-2xl grayscale opacity-70" alt="Regalos" />
          </div>
        </div>
      </section>

      {/* Confirmación de Asistencia (RSVP) - REUBICADO */}
      <section id="rsvp" className="py-24 px-6 text-center bg-[#fdfbf7] relative">
        <div className="max-w-3xl mx-auto space-y-12 animate-fade-up">
          <Calendar className="mx-auto opacity-20" size={48} style={{ color: colors.navyBlue }} />
          <h2 className="text-5xl font-serif italic text-slate-800">Confirmación de Asistencia</h2>
          
          <div className="bg-slate-100/50 p-2 rounded-full inline-flex mb-8">
            <button onClick={() => setRsvpMethod('direct')} className={`px-10 py-3 rounded-full text-sm font-semibold transition-all font-sans ${rsvpMethod === 'direct' ? 'bg-white shadow-lg text-slate-900' : 'text-slate-400'}`}>Confirmación Directa</button>
            <button onClick={() => setRsvpMethod('whatsapp')} className={`px-10 py-3 rounded-full text-sm font-semibold transition-all font-sans ${rsvpMethod === 'whatsapp' ? 'bg-white shadow-lg text-slate-900' : 'text-slate-400'}`}>Vía WhatsApp</button>
          </div>

          {!submitted ? (
            <div className="max-w-md mx-auto">
              {rsvpMethod === 'direct' ? (
                <form onSubmit={handleDirectConfirm} className="bg-white p-10 rounded-3xl shadow-2xl border border-slate-100 text-left space-y-8">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold ml-1">Hola {params.nombre}, confirma tu nombre</label>
                    <input type="text" value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} className="w-full pl-6 pr-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-slate-100 outline-none transition-all" required />
                  </div>
                  <div className="space-y-6">
                    <div className="flex justify-between items-end"><label className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold ml-1">Pases</label><span className="text-4xl font-serif italic text-slate-800">{formData.pasesConfirmados}</span></div>
                    <input type="range" min="1" max={params.pases} value={formData.pasesConfirmados} onChange={(e) => setFormData({...formData, pasesConfirmados: parseInt(e.target.value)})} className="rsvp-slider w-full appearance-none cursor-pointer" />
                  </div>
                  {submitError && (
                    <div className="flex items-center space-x-2 text-rose-500 bg-rose-50 p-4 rounded-xl text-sm font-sans italic">
                       <span>Hubo un error al guardar. Intenta vía WhatsApp.</span>
                    </div>
                  )}
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 rounded-2xl text-white font-bold uppercase tracking-[0.2em] text-[10px] flex items-center justify-center space-x-3 transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-navy-200" style={{ backgroundColor: '#1a2b44' }}>
                    {isSubmitting ? <Clock className="animate-spin" size={18} /> : <Send size={18} />}
                    <span>Confirmar ahora</span>
                  </button>
                </form>
              ) : (
                <a href={mensajeWA} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-3 px-10 py-5 text-white font-sans uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all hover:scale-105 no-underline w-full shadow-2xl shadow-green-100" style={{ backgroundColor: '#25D366' }}>
                  <MessageCircle size={20} /><span>Confirmar por WhatsApp</span>
                </a>
              )}
            </div>
          ) : (
            <div className="animate-fade-up bg-green-50 p-12 rounded-[3rem] border border-green-100 max-w-md mx-auto text-center">
              <CheckCircle className="text-green-500 mx-auto mb-6" size={40} />
              <h3 className="text-2xl font-serif mb-2 text-slate-800">¡Confirmación Exitosa!</h3>
              <p className="text-slate-600">Muchas gracias, {formData.nombre}.</p>
            </div>
          )}
        </div>
      </section>

      {/* --- RECOMENDACIONES Y CIERRE --- */}
      <section className="py-24 px-6 bg-white text-center border-t border-slate-100">
        <div className="max-w-4xl mx-auto space-y-16 animate-fade-up">
          <div className="space-y-8">
            <Info size={40} className="mx-auto text-slate-300" />
            <h2 className="text-4xl font-serif italic text-slate-800">Recomendaciones</h2>
            <div className="grid md:grid-cols-2 gap-8 text-left max-w-2xl mx-auto font-book">
               <div className="p-6 bg-slate-50 rounded-2xl flex items-start space-x-4">
                 <div className="p-2 bg-white rounded-full shadow-sm"><Clock size={16} style={{ color: colors.gold }} /></div>
                 <p className="text-slate-600 text-sm">Le pedimos amablemente ser puntual para disfrutar cada momento del evento.</p>
               </div>
               <div className="p-6 bg-slate-50 rounded-2xl flex items-start space-x-4">
                 <div className="p-2 bg-white rounded-full shadow-sm"><User size={16} style={{ color: colors.gold }} /></div>
                 <p className="text-slate-600 text-sm">Por favor, siga las instrucciones del personal de la Hacienda para una mejor experiencia.</p>
               </div>
            </div>
          </div>

          <div className="space-y-6 pt-12">
            <h3 className="text-3xl md:text-5xl font-serif italic text-slate-900 leading-tight">
              Esperamos contar con su presencia
            </h3>
            <p className="text-xl font-serif text-slate-400 tracking-[0.4em] uppercase">
              MUCHAS GRACIAS
            </p>
          </div>
        </div>
      </section>

      {/* Foto Final */}
      <section className="py-12 px-6 bg-white mb-24">
        <div className="max-w-4xl mx-auto rounded-[3rem] overflow-hidden shadow-2xl relative group">
          <img 
            src="/boda42.jpg" 
            alt="Nashelly & Salvador" 
            className="w-full h-auto object-cover min-h-[500px]"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=2000"; }}
          />
          <div className="absolute inset-0 bg-black/10 pointer-events-none"></div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-24 text-center bg-[#1a2b44] text-white">
        <div className="space-y-6">
          <p className="text-6xl font-serif italic text-white/90">Nashelly & Salvador</p>
          <div className="h-px w-32 bg-white/20 mx-auto"></div>
          <p className="text-xs uppercase tracking-[0.5em] text-white/50 font-sans">01.08.2026 • Hacienda San Andrés</p>
        </div>
      </footer>

      {/* Menú flotante móvil */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur shadow-2xl rounded-full px-8 py-4 flex space-x-8 z-50 md:hidden border border-slate-200">
        <button onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="text-slate-400 hover:text-slate-900 transition-colors"><Camera size={20} /></button>
        <button onClick={() => window.scrollTo({top: 1500, behavior: 'smooth'})} className="text-slate-400 hover:text-slate-900 transition-colors"><MapPin size={20} /></button>
        <button onClick={() => window.scrollTo({top: 3500, behavior: 'smooth'})} className="text-slate-400 hover:text-slate-900 transition-colors"><Calendar size={20} /></button>
        <button onClick={() => window.scrollTo({top: 4500, behavior: 'smooth'})} className="text-slate-400 hover:text-slate-900 transition-colors"><Heart size={20} /></button>
      </div>
    </div>
  );
};

export default App;